import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { positionResponsiveGalleries } from '@theme/na-responsive-gallery';
import { ColorMediaPrefetcher } from '@theme/media-gallery-prefetch';

const COLOR_INPUT_SELECTOR = '.color-option--swatch input, input.na-form__input--check-color';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
    /** @type {NodeListOf<HTMLElement> | null} */
    #colorTaggedElements = null;

    /** @type {import('@theme/media-gallery-prefetch').ColorMediaMap | null} */
    #colorMediaMap = null;

    /** @type {ColorMediaPrefetcher | null} */
    #prefetcher = null;

    /** @type {string | null} — slide-id to re-select after morph, set by #handleVariantSelected */
    #postMorphSlideId = null;

    connectedCallback() {
        super.connectedCallback();

        const { signal } = this.#controller;
        const target = this.closest('.shopify-section, dialog');

        target?.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#handleVariantUpdate), {
            signal,
        });
        target?.addEventListener(
            ThemeEvents.variantSelected,
            /** @type {EventListener} */ (this.#handleVariantSelected),
            { signal }
        );
        target?.addEventListener(ThemeEvents.variantUpdateFailed, () => this.#setLoading(false), { signal });
        this.refs.zoomDialogComponent?.addEventListener(
            ThemeEvents.zoomMediaSelected,
            /** @type {EventListener} */ (/** @type {unknown} */ (this.#handleZoomMediaSelected)),
            {
                signal,
            }
        );

        this.#colorTaggedElements = this.querySelectorAll('[data-color-tag]');

        const colorMapScript = this.querySelector('script[data-color-media-map]');
        if (colorMapScript?.textContent) {
            try {
                const parsed = JSON.parse(colorMapScript.textContent);
                if (ColorMediaPrefetcher.isMap(parsed)) this.#colorMediaMap = parsed;
            } catch (error) {
                console.warn('media-gallery: invalid color media map', error);
            }
        }

        if (this.#colorMediaMap) {
            this.#prefetcher = new ColorMediaPrefetcher(this.#colorMediaMap);
            this.#prefetcher.warmAll(() => this.getClientRects().length > 0);
            target?.addEventListener('pointerover', this.#handleColorIntent, { signal, passive: true });
            target?.addEventListener('focusin', this.#handleColorIntent, { signal });
        }
    }

    /**
     * Hover or focus on a swatch is the earliest signal of which color comes next,
     * so that color jumps to the front of the prefetch queue.
     * @param {Event} event
     */
    #handleColorIntent = (event) => {
        if (!(event.target instanceof Element)) return;
        const label = event.target.closest('label, ' + COLOR_INPUT_SELECTOR);
        const input = label instanceof HTMLInputElement ? label : label?.querySelector(COLOR_INPUT_SELECTOR);
        if (!(input instanceof HTMLInputElement)) return;

        const color = normalizeColor(input.value);
        if (color) this.#prefetcher?.prioritize(color);
    };

    #controller = new AbortController();

    disconnectedCallback() {
        super.disconnectedCallback();

        this.#controller.abort();
    }

    // ─── Event Handlers ──────────────────────────────────────────────────

    /**
     * Handles the variant:selected event (fires instantly on swatch click).
     * Instantly toggles visibility of variant media to achieve a 0ms update delay.
     *
     * @param {import('@theme/events').VariantSelectedEvent} event
     */
    #handleVariantSelected = (event) => {
        const optionValueId = event.detail?.resource?.id;
        if (!optionValueId) return;

        const target = this.closest('.shopify-section, dialog');
        const input = target?.querySelector(`[data-option-value-id="${optionValueId}"]`);
        if (!(input instanceof HTMLInputElement)) return;

        // Only filter for color option changes
        const isColorOption =
            input.closest('.color-option--swatch') || input.classList.contains('na-form__input--check-color');
        if (!isColorOption) return;

        const colorValue = normalizeColor(input.value);

        if (!colorValue) return;

        // Instantly update DOM visibility
        if (!this.#colorTaggedElements) {
            this.#colorTaggedElements = this.querySelectorAll('[data-color-tag]');
        }

        let hasMatchingElements = false;
        let hasColorTaggedMedia = false;
        this.#colorTaggedElements.forEach((el) => {
            const tag = el.getAttribute('data-color-tag');
            if (!tag || tag === colorValue) {
                el.removeAttribute('hidden');
                hasMatchingElements = true;
                if (tag) hasColorTaggedMedia = true;
            } else {
                el.setAttribute('hidden', '');
            }
        });

        const mapEntry = this.#colorMediaMap?.colors[colorValue];
        // The color's first image leads; the variant's featured media can sit further down the set.
        const featuredSlideId = hasColorTaggedMedia ? null : mapEntry?.id;
        this.#prefetcher?.prioritize(colorValue);
        this.#setLoading(true);

        // Navigate to the featured slide if it's already in the DOM, otherwise fall back
        // to the first visible slide. The morph + select(0) in #handleVariantUpdate handles
        // the case where the slide doesn't exist yet.
        const { slideshow } = this;
        if (hasMatchingElements && slideshow) {
            if (featuredSlideId) {
                // Remember which slide we navigated to so #handleVariantUpdate can re-select
                // the same element after morph (same-slide guard makes it a no-op → no jump).
                this.#postMorphSlideId = String(featuredSlideId);
            } else {
                this.#postMorphSlideId = null;
            }
            requestAnimationFrame(() => {
                if (featuredSlideId) {
                    // force: true bypasses the same-slide guard so the indicator always
                    // updates even when the new featured slide lands at the same index (0)
                    // as the previous one — which happens when the user hasn't scrolled.
                    slideshow.select({ id: String(featuredSlideId) }, undefined, { animate: false, force: true });
                } else {
                    slideshow.select(0, undefined, { animate: false, force: true });
                }
            });
        }
    };

    /**
     * Handles a variant update event by morphing the current media gallery with the new one.
     * Includes race-condition protection to discard outdated server responses.
     *
     * @param {VariantUpdateEvent} event - The variant update event.
     */
    #handleVariantUpdate = (event) => {
        const source = event.detail.data.html;
        const variant = event.detail.resource;

        if (!source || !variant) return;

        // Shopify's variant JSON has options as an array of value strings (not objects),
        // so we can't reliably extract the color option name here. The AbortController
        // in variant-picker already cancels in-flight fetches for superseded variants,
        // so we always morph with whatever server response arrives.

        // The response has every gallery block; morph from this block's own so zoom-dialog ids don't cross-wire.
        const { blockId } = this.dataset;
        const newMediaGallery = blockId
            ? source.querySelector(`media-gallery[data-block-id="${blockId}"]`)
            : source.querySelector('media-gallery');
        if (!newMediaGallery) return;

        morph(this, newMediaGallery, MORPH_OPTIONS);
        positionResponsiveGalleries(this.closest('.shopify-section') || document);
        this.#setLoading(false);

        // morph walks childNodes, which are empty on <template>, so its content must be synced by hand
        const newDeferredMedia = newMediaGallery.querySelector('template[ref="deferredMedia"]');
        const oldDeferredMedia = this.querySelector('template[ref="deferredMedia"]');
        if (newDeferredMedia instanceof HTMLTemplateElement && oldDeferredMedia instanceof HTMLTemplateElement) {
            oldDeferredMedia.content.replaceChildren(newDeferredMedia.content);
        }

        // Refresh the cached elements after morphing
        this.#colorTaggedElements = this.querySelectorAll('[data-color-tag]');

        // Re-select the correct slide after morph. Prefer the same slide ID that was
        // navigated to instantly in #handleVariantSelected — select()'s same-slide guard
        // will make it a no-op if the element reference is unchanged, avoiding a visible
        // indicator jump. Fall back to select(0) for non-color variant changes.
        const selectId = this.#postMorphSlideId;
        this.#postMorphSlideId = null;
        const { slideshow } = this;
        if (slideshow) {
            requestAnimationFrame(() => {
                if (selectId) {
                    slideshow.select({ id: selectId }, undefined, { animate: false });
                } else {
                    // Morph reorders slides under the current scroll offset, so index 0 must be forced.
                    slideshow.select(0, undefined, { animate: false, force: true });
                }
            });
        }
    };

    /**
     * The overlay is CSS-delayed, so cached variants that morph within a few ms never show it.
     * @param {boolean} loading
     */
    #setLoading(loading) {
        this.classList.toggle('media-gallery--loading', loading);
        this.refs.slideshow?.setAttribute('aria-busy', String(loading));
    }

    /**
     * Handles the 'zoom-media:selected' event.
     * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
     */
    #handleZoomMediaSelected = async (event) => {
        this.slideshow?.select(event.detail.index, undefined, { animate: false });
    };

    // ─── Public API ──────────────────────────────────────────────────────

    /**
     * Zooms the media gallery.
     *
     * @param {number} index - The index of the media to zoom.
     * @param {PointerEvent} event - The pointer event.
     */
    zoom(index, event) {
        this.refs.zoomDialogComponent?.open(index, event);
    }

    /**
     * Preloads an image.
     * @param {number} index - The index of the media to preload.
     */
    preloadImage(index) {
        const zoomDialog = this.refs.zoomDialogComponent;
        const zoomDialogMedia = zoomDialog?.hydrate()[index];
        if (!zoomDialogMedia) return;

        zoomDialog?.loadHighResolutionImage(zoomDialogMedia);
    }

    get slideshow() {
        return this.refs.slideshow;
    }

    get media() {
        return this.refs.media;
    }

    get presentation() {
        return this.dataset.presentation;
    }
}

/**
 * Mirrors the Liquid normalisation (strip | downcase | replace " " → "_") used for alt color tags.
 * @param {string | undefined} value
 */
function normalizeColor(value) {
    return value?.trim().toLowerCase().replace(/\s+/g, '_') ?? '';
}

positionResponsiveGalleries();

if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', MediaGallery);
}
